import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { cakeConfigOptions } from '../../mock';

const CakeConfigurator = ({
  cakeConfigDialog,
  setCakeConfigDialog,
  selectedCake,
  cakeConfig,
  setCakeConfig,
  isCakeConfigComplete,
  getCakePrice,
  addCakeToCart
}) => {
  return (
    <Dialog open={cakeConfigDialog} onOpenChange={setCakeConfigDialog}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">🎂</span>
            Personaliza tu {selectedCake?.name}
          </DialogTitle>
          <DialogDescription>
            Configura tu pastel perfecto en 4 pasos
          </DialogDescription>
        </DialogHeader>
        
        {selectedCake && (
          <div className="space-y-6 py-4">
            {(selectedCake.name === 'La Reina' || selectedCake.isConfigurable) && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <p className="text-sm font-bold text-red-900 flex items-center gap-2">
                  <span className="text-xl">⏰</span>
                  Aviso Importante
                </p>
                <p className="text-sm text-red-800 mt-2">
                  Este pedido requiere al menos 24 horas para su preparación y debe recibir confirmación de inicio de preparación
                </p>
              </div>
            )}
            
            {/* Paso 1: Tamaño */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <span className="bg-caramel text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
                Elige el tamaño
              </Label>
              <div className="grid grid-cols-2 gap-4">
                {selectedCake.sizes?.map(size => (
                  <button
                    key={size.id}
                    type="button"
                    onClick={() => setCakeConfig({ ...cakeConfig, size: size.id })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      cakeConfig.size === size.id 
                        ? 'border-caramel bg-amber-50' 
                        : 'border-gray-200 hover:border-caramel'
                    }`}
                  >
                    <div className="relative w-16 h-16 mx-auto mb-2">
                      <svg viewBox="0 0 100 100" className="w-full h-full">
                        <circle cx="50" cy="50" r="45" fill="#FDF2E9" stroke={cakeConfig.size === size.id ? '#EC4899' : '#D4A574'} strokeWidth="2"/>
                        {Array.from({ length: size.id === 'mediana' ? 12 : 20 }).map((_, i) => {
                          const angle = (i * 360 / (size.id === 'mediana' ? 12 : 20)) * Math.PI / 180;
                          const x2 = 50 + 35 * Math.cos(angle);
                          const y2 = 50 + 35 * Math.sin(angle);
                          return (
                            <line key={i} x1="50" y1="50" x2={x2} y2={y2} stroke={cakeConfig.size === size.id ? '#EC4899' : '#D4A574'} strokeWidth="1" opacity="0.5"/>
                          );
                        })}
                        <circle cx="50" cy="50" r="8" fill={cakeConfig.size === size.id ? '#EC4899' : '#D4A574'}/>
                      </svg>
                    </div>
                    <div className="font-bold text-chocolate">{size.name}</div>
                    <div className="text-xs text-chocolate-light">{size.portions}</div>
                    <div className="text-lg font-bold text-caramel mt-1">${size.price}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Paso 2: Sabor */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <span className="bg-caramel text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
                Sabor del bizcocho
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {cakeConfigOptions.sabores.map(sabor => (
                  <button
                    key={sabor.id}
                    type="button"
                    onClick={() => setCakeConfig({ ...cakeConfig, sabor: sabor.id })}
                    className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                      cakeConfig.sabor === sabor.id 
                        ? 'border-caramel bg-amber-50' 
                        : 'border-gray-200 hover:border-caramel'
                    }`}
                  >
                    <div 
                      className="w-6 h-6 rounded-full border border-gray-300"
                      style={{ background: sabor.color }}
                    />
                    <span className="font-medium text-sm">{sabor.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Paso 3: Relleno */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <span className="bg-caramel text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
                Relleno
              </Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {cakeConfigOptions.rellenos.map(relleno => (
                  <button
                    key={relleno.id}
                    type="button"
                    onClick={() => setCakeConfig({ ...cakeConfig, relleno: relleno.id })}
                    className={`p-2 rounded-lg border-2 text-xs font-medium transition-all ${
                      cakeConfig.relleno === relleno.id 
                        ? 'border-caramel bg-amber-50 text-amber-700' 
                        : 'border-gray-200 hover:border-caramel text-chocolate'
                    }`}
                  >
                    {relleno.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Paso 4: Exterior */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <span className="bg-caramel text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">4</span>
                Cobertura exterior
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {cakeConfigOptions.exteriores.map(exterior => (
                  <button
                    key={exterior.id}
                    type="button"
                    onClick={() => setCakeConfig({ ...cakeConfig, exterior: exterior.id })}
                    className={`p-2 rounded-lg border-2 text-xs font-medium transition-all ${
                      cakeConfig.exterior === exterior.id 
                        ? 'border-caramel bg-amber-50 text-amber-700' 
                        : 'border-gray-200 hover:border-caramel text-chocolate'
                    }`}
                  >
                    {exterior.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Paso 5: Topping */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <span className="bg-caramel text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">5</span>
                Topping
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {cakeConfigOptions.toppings.map(topping => (
                  <button
                    key={topping.id}
                    type="button"
                    onClick={() => setCakeConfig({ ...cakeConfig, topping: topping.id })}
                    className={`p-2 rounded-lg border-2 text-xs font-medium transition-all ${
                      cakeConfig.topping === topping.id 
                        ? 'border-caramel bg-amber-50 text-amber-700' 
                        : 'border-gray-200 hover:border-caramel text-chocolate'
                    }`}
                  >
                    {topping.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Resumen */}
            <div className="bg-gradient-to-r from-amber-50 to-cream rounded-xl p-4 border border-amber-200">
              <div className="text-sm font-semibold text-chocolate mb-2">Tu selección:</div>
              <div className="text-xs text-chocolate-light space-y-1">
                <div>📏 {selectedCake.sizes?.find(s => s.id === cakeConfig.size)?.name} ({selectedCake.sizes?.find(s => s.id === cakeConfig.size)?.portions})</div>
                <div>🎂 {cakeConfigOptions.sabores.find(s => s.id === cakeConfig.sabor)?.name || '(selecciona)'}</div>
                <div>🥄 {cakeConfigOptions.rellenos.find(r => r.id === cakeConfig.relleno)?.name || '(selecciona)'}</div>
                <div>🎨 {cakeConfigOptions.exteriores.find(e => e.id === cakeConfig.exterior)?.name || '(selecciona)'}</div>
                <div>✨ {cakeConfigOptions.toppings.find(t => t.id === cakeConfig.topping)?.name || '(selecciona)'}</div>
              </div>
              <div className="text-2xl font-bold text-caramel text-center mt-3">
                ${getCakePrice().toFixed(2)}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={addCakeToCart}
            disabled={!isCakeConfigComplete()}
            className={`w-full py-6 text-lg ${
              isCakeConfigComplete() 
                ? 'bg-caramel hover:bg-amber-600 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isCakeConfigComplete() 
              ? '🎂 Agregar al Pedido' 
              : 'Completa todas las opciones'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CakeConfigurator;
